/**
 * Google Analytics 4 (GA4) — SPA route tracking.
 *
 * Script is injected after idle (not in index.html) so it stays off the map
 * critical path. REACT_APP_GA_MEASUREMENT_ID must match the gtag id.
 */

const MEASUREMENT_ID = (process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-054YN9BW3J').trim();

/** True when a valid G-… measurement id is configured at build time. */
export function isGoogleAnalyticsEnabled(): boolean {
    return /^G-[A-Z0-9]+$/.test(MEASUREMENT_ID);
}

export function getGaMeasurementId(): string | null {
    return isGoogleAnalyticsEnabled() ? MEASUREMENT_ID : null;
}

declare global {
    interface Window {
        dataLayer?: unknown[];
        gtag?: (...args: unknown[]) => void;
    }
}

let loadStarted = false;

function ensureGtagStub(): void {
    window.dataLayer = window.dataLayer || [];
    if (typeof window.gtag !== 'function') {
        window.gtag = function gtag(...args: unknown[]) {
            window.dataLayer!.push(args);
        };
    }
}

/** Load gtag.js after the main thread is idle (or after a short fallback delay). */
export function loadGoogleAnalytics(): void {
    const id = getGaMeasurementId();
    if (!id || loadStarted) return;
    if (document.querySelector('script[data-empire-ga]')) {
        loadStarted = true;
        return;
    }
    loadStarted = true;

    const inject = () => {
        ensureGtagStub();
        window.gtag!('js', new Date());
        window.gtag!('config', id);

        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
        script.dataset.empireGa = '1';
        document.head.appendChild(script);
    };

    if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(() => inject(), { timeout: 4000 });
    } else {
        window.setTimeout(inject, 2500);
    }
}

/** Record a SPA route change (pathname + query). */
export function trackPageView(pagePath: string): void {
    const id = getGaMeasurementId();
    if (!id || typeof window.gtag !== 'function') return;
    window.gtag('config', id, {
        page_path: pagePath,
        page_location: `${window.location.origin}${pagePath}`,
    });
}

/** Record a custom GA4 event (no-op when GA is not configured). */
export function trackEvent(
    eventName: string,
    params: Record<string, string | number | boolean> = {},
): void {
    if (!isGoogleAnalyticsEnabled() || typeof window.gtag !== 'function') return;
    window.gtag('event', eventName, params);
}
